/**
 * Мост к нативному приложению (Flutter), внутри которого это приложение
 * открыто в WebView. Устроен как в progress-app-new: обработчики живут на
 * `window.flutter_inappwebview` (пакет flutter_inappwebview) и существуют только
 * внутри оболочки. В браузере и в Telegram каждый вызов здесь ничего не делает,
 * а экран переходит на своё веб-поведение.
 *
 * Обработчики, которые должна зарегистрировать оболочка (`addJavaScriptHandler`):
 *
 * | Имя                        | Аргумент                  | Что делает / что вернуть                    |
 * | -------------------------- | ------------------------- | ------------------------------------------- |
 * | `shareProduct`             | `{ url }`                 | системное окно «Поделиться» для ссылки      |
 * | `openMaps`                 | `{ latitude, longitude }` | открыть точку в картах телефона             |
 * | `notification.getToken`    | —                         | токен Firebase телефона для push — см.      |
 * |                            |                           | `nativePushToken`                           |
 * | `notification.subscribe`   | `{ userId }`              | устарело: тема `customer_{id}` — только     |
 * | `notification.unsubscribe` | `{ userId }`              | пока выключен флаг VITE_FEATURE_PUSH        |
 * | `getLocation`              | —                         | вернуть `{ latitude, longitude }` или null  |
 * | `openUrl`                  | `{ url }`                 | открыть страницу вне WebView (оплата)       |
 * | `vibrate`                  | `{ type }`                | отдача: selection, light, medium, heavy,    |
 * |                            |                           | success, warning, error (`haptic()` в ui)   |
 * | `security.request`         | `{ version, requestId,    | PIN-код, биометрия и язык оболочки — см.    |
 * |                            |   action, payload }`      | `requestNative`                             |
 * | `security.sessionChanged`  | `{ authenticated, … }`    | кто вошёл / вышел — для PIN-кода оболочки   |
 * | `security.logoutAck`       | `{ version, requestId,    | выход по просьбе оболочки выполнен          |
 * |                            |   ok, error? }`           |                                             |
 * | `datePicker.open`          | `{ initialDate?, minDate?,| окно выбора даты; `{ ok, date }` или отказ —|
 * |                            |   maxDate? }`             | см. `pickNativeDate`                        |
 *
 * Сплэш оболочка убирает по `window.notifyAppReady()` — функцию она
 * подставляет сама (`notifyAppReady` ниже).
 *
 * Аргумент — всегда один объект: позиционные аргументы в progress-app-new до
 * нативной стороны не доходили.
 */

interface FlutterInAppWebView {
  callHandler: (handlerName: string, ...args: unknown[]) => Promise<unknown>
}

declare global {
  interface Window {
    flutter_inappwebview?: FlutterInAppWebView
    /** Подставляет оболочка: первый экран нарисован, сплэш можно убрать. */
    notifyAppReady?: () => void
  }
}

export const READY_EVENT = 'flutterInAppWebViewPlatformReady'

/**
 * Сколько ждать события готовности, прежде чем считать хозяина обычным
 * браузером. Оболочка шлёт его, как только страница начала грузиться, так что
 * это запас для медленного телефона.
 */
export const READY_TIMEOUT = 5000

let readyPromise: Promise<FlutterInAppWebView | null> | null = null

/** Мост как он есть сейчас, без ожидания события готовности. */
const currentBridge = (): FlutterInAppWebView | null =>
  typeof window.flutter_inappwebview?.callHandler === 'function'
    ? window.flutter_inappwebview
    : null

/** Внутри нативной оболочки, а не в браузере. */
export const isNativeShell = (): boolean => currentBridge() !== null

/**
 * Мост, как только оболочка сообщит о готовности, или null, если оболочки нет.
 * Промах не запоминаем: браузер снова дождётся таймаута, а медленную оболочку
 * найдёт следующий вызов.
 */
export function whenBridgeReady(): Promise<FlutterInAppWebView | null> {
  if (readyPromise) return readyPromise

  readyPromise = new Promise((resolve) => {
    const bridge = currentBridge()
    if (bridge) {
      resolve(bridge)
      return
    }

    const onReady = () => {
      window.clearTimeout(timer)
      resolve(currentBridge())
    }

    const timer = window.setTimeout(() => {
      window.removeEventListener(READY_EVENT, onReady)
      readyPromise = null
      resolve(null)
    }, READY_TIMEOUT)

    window.addEventListener(READY_EVENT, onReady, { once: true })
  })

  return readyPromise
}

/** Вызвать обработчик, когда мост готов; нет моста или он упал — undefined. */
async function callNative(handlerName: string, ...args: unknown[]): Promise<unknown> {
  const bridge = await whenBridgeReady()
  if (!bridge) return undefined
  try {
    return await bridge.callHandler(handlerName, ...args)
  } catch (error) {
    // Сбой моста не должен ронять действие вокруг него.
    console.warn(`[native] ${handlerName} failed`, error)
    return undefined
  }
}

/**
 * Вызвать обработчик, только если мост уже есть, и сказать, был ли он. Для
 * нажатий: вызывающему нужен ответ сразу, чтобы перейти на веб-поведение, а
 * ждать таймаут готовности значило бы кнопку, которая секунды ничего не делает.
 */
function callNativeNow(handlerName: string, ...args: unknown[]): boolean {
  if (!currentBridge()) return false
  void callNative(handlerName, ...args)
  return true
}

/** Открыть точку в картах телефона — маршрут до аптеки. */
export const openNativeMaps = (latitude: number, longitude: number): boolean =>
  callNativeNow('openMaps', { latitude, longitude })

/**
 * Открыть страницу вне WebView — страницу оплаты. `window.open` во Flutter
 * WebView без своей обработки ничего не открывает, и кнопка «Оплатить» молчала бы.
 */
export const openNativeUrl = (url: string): boolean => callNativeNow('openUrl', { url })

/**
 * Push: оболочка подписывает телефон на тему `customer_{userId}` в Firebase.
 * Кто вошёл, она сама не знает — id отдаём после входа и при каждом запуске,
 * забираем перед выходом.
 */
export const subscribeToNotifications = (userId: number): Promise<unknown> =>
  userId ? callNative('notification.subscribe', { userId: String(userId) }) : Promise.resolve()

export const unsubscribeFromNotifications = (userId: number): Promise<unknown> =>
  userId ? callNative('notification.unsubscribe', { userId: String(userId) }) : Promise.resolve()

/**
 * Push по токену (docs/notes/flutter-bridge.md › Push). Токен Firebase живёт в
 * оболочке, сессия — у нас: оболочка отдаёт токен, а за покупателем его
 * закрепляем мы (`POST /push/tokens`). Сменился токен — оболочка шлёт событие.
 */
export const NATIVE_PUSH_TOKEN_EVENT = 'dorixona-native-push-token-changed'

/** Push пришёл, пока приложение открыто; `detail` — его `data` (`template`, `order_id`). */
export const NATIVE_PUSH_RECEIVED_EVENT = 'dorixona-native-push-received'

export interface NativePushToken {
  token: string
  platform: 'android' | 'ios'
}

interface PushTokenAnswer {
  ok?: unknown
  token?: unknown
  platform?: unknown
}

/** Токен из ответа или события оболочки; непонятный ответ — null. */
export function toPushToken(answer: unknown): NativePushToken | null {
  const { token, platform } = (answer ?? {}) as PushTokenAnswer
  if (typeof token !== 'string' || token === '') return null
  return { token, platform: platform === 'ios' ? 'ios' : 'android' }
}

/**
 * Токен этого телефона. Первый вызов показывает системный вопрос о
 * разрешении, поэтому может идти секунды — экран его не ждёт. null — оболочки
 * нет или токена сейчас нет (`ok: false`): спросим при следующем запуске.
 * Уведомления выключены в настройках (`denied` при `ok: true`) — токен всё
 * равно отдаём: push пойдут, как только покупатель их разрешит.
 */
export async function nativePushToken(): Promise<NativePushToken | null> {
  const answer = await callNative('notification.getToken')
  if ((answer as PushTokenAnswer | null | undefined)?.ok !== true) return null
  return toPushToken(answer)
}

/**
 * Системное окно «Поделиться». false — оболочка не взяла, и экран поделится
 * по-веб. Форма аргумента у оболочки неизвестна заранее, поэтому, как в
 * progress-app-new, сначала объект `{ url }`, потом строка.
 */
export async function shareUrlNatively(url: string): Promise<boolean> {
  const bridge = currentBridge()
  if (!bridge) return false
  for (const args of [[{ url }], [url]]) {
    try {
      await bridge.callHandler('shareProduct', ...args)
      return true
    } catch (error) {
      console.warn('[native] shareProduct failed', error)
    }
  }
  return false
}

export interface GeoPoint {
  latitude: number
  longitude: number
}

/**
 * Где сейчас телефон — точка из GPS оболочки. null — оболочки нет, доступ к
 * геолокации не дали или точка не пришла; тогда экран спросит браузер.
 */
export async function nativeLocation(): Promise<GeoPoint | null> {
  if (!currentBridge()) return null
  const answer = (await callNative('getLocation')) as Partial<GeoPoint> | null | undefined
  const { latitude, longitude } = answer ?? {}
  if (typeof latitude !== 'number' || typeof longitude !== 'number') return null
  return { latitude, longitude }
}

/**
 * Отказ оболочки. `code` — её код (`CANCELLED`, `BUSY`, `LOCKED`, `PIN_REQUIRED`,
 * `BIOMETRIC_UNAVAILABLE`, `UNAUTHORIZED`, `STORAGE_ERROR`, …) или наш:
 * `UNAVAILABLE` — оболочки нет, `TIMEOUT` — не ответила, `INVALID_RESPONSE` —
 * ответила не тем (старая сборка без `security.request`).
 */
export class NativeError extends Error {
  constructor(readonly code: string) {
    super(code)
    this.name = 'NativeError'
  }
}

interface NativeResponse<T> {
  version: 1
  requestId: string
  action: string
  ok: boolean
  data?: T
  error?: { code: string }
}

/** Сколько ждать ответа: окно PIN-кода оболочки отвечает, когда его закроют. */
export const NATIVE_TIMEOUT = 15_000

/** Номер запроса уникален и между перезагрузками страницы: оболочка живёт дольше неё. */
const requestPrefix = Math.random().toString(36).slice(2, 10)
let sequence = 0

/**
 * Запрос к оболочке через `security.request`: конверт с версией и номером,
 * ответ — `data` или отказ с кодом. Отменить окно оболочки нельзя: после
 * таймаута её поздний ответ просто никто не ждёт, а следующий запрос может
 * получить `BUSY`, пока окно открыто.
 */
export async function requestNative<T>(
  action: string,
  payload: Record<string, unknown> = {},
  timeoutMs = NATIVE_TIMEOUT,
): Promise<T> {
  const bridge = currentBridge()
  if (!bridge) throw new NativeError('UNAVAILABLE')
  const requestId = `${requestPrefix}-${++sequence}`
  let timer = 0
  const timeout = new Promise<never>((_, reject) => {
    timer = window.setTimeout(() => {
      reject(new NativeError('TIMEOUT'))
    }, timeoutMs)
  })
  try {
    const response = (await Promise.race([
      bridge.callHandler('security.request', { version: 1, requestId, action, payload }),
      timeout,
    ])) as NativeResponse<T> | null | undefined
    if (response?.version !== 1 || response.requestId !== requestId) {
      throw new NativeError('INVALID_RESPONSE')
    }
    if (!response.ok) throw new NativeError(response.error?.code ?? 'NATIVE_ERROR')
    return response.data as T
  } finally {
    window.clearTimeout(timer)
  }
}

/** Код отказа оболочки; сбой самого вызова — `NATIVE_ERROR`. */
export const nativeErrorCode = (error: unknown): string =>
  error instanceof NativeError ? error.code : 'NATIVE_ERROR'

/**
 * Язык оболочки: её экран PIN-кода и окна говорят на том же языке, что и
 * страница. Шлём выбор покупателя, когда он выбрал язык, и при запуске, если
 * выбор уже был. Оболочки нет или она старая — ничего не случилось.
 */
export async function setNativeLanguage(language: string): Promise<void> {
  if (!currentBridge()) return
  try {
    await requestNative('app.setLanguage', { language })
  } catch (error) {
    console.warn('[native] app.setLanguage failed', error)
  }
}

/**
 * PIN-код оболочки привязан к аккаунту: после входа отдаём ей id покупателя
 * (сменился — она забудет PIN прежнего), после выхода — что вышли, и она
 * сотрёт PIN и биометрию. Язык оболочки выход не трогает.
 */
export const nativeSignedIn = (userId: number): Promise<unknown> =>
  userId
    ? callNative('security.sessionChanged', { authenticated: true, userId: String(userId) })
    : Promise.resolve()

/**
 * Вошёл, а PIN-кода нет — оболочка сразу открывает окно установки. `status` —
 * ответ `security.sessionChanged`. Закрыл окно — спросим снова при следующем
 * запуске или входе. Старая оболочка состояния не отдаёт — ничего не делаем.
 */
export async function requirePinSetup(status: unknown): Promise<void> {
  if ((status as { hasPin?: unknown } | null | undefined)?.hasPin !== false) return
  try {
    await requestNative('security.setupPin')
  } catch (error) {
    if (nativeErrorCode(error) !== 'CANCELLED')
      console.warn('[native] security.setupPin failed', error)
  }
}

export const nativeSignedOut = (): Promise<unknown> =>
  callNative('security.sessionChanged', { authenticated: false, logout: true })

/**
 * Оболочка просит выйти: покупатель забыл PIN-код или трижды ввёл неверный.
 * Событие приходит в страницу; `requestId` возвращаем в подтверждении как есть.
 */
export const NATIVE_LOGOUT_EVENT = 'dorixona-native-security-logout-request'

export interface NativeLogoutRequest {
  version: 1
  requestId: string
  reason: 'pin_forgotten' | 'pin_failed'
}

/**
 * Подтвердить выход оболочке — только когда токены уже стёрты. Без
 * подтверждения (или с `ok: false`) она держит экран закрытым.
 */
export const acknowledgeNativeLogout = (requestId: string, ok: boolean): Promise<unknown> =>
  callNative(
    'security.logoutAck',
    ok
      ? { version: 1, requestId, ok }
      : { version: 1, requestId, ok, error: { code: 'FRONTEND_LOGOUT_FAILED' } },
  )

/**
 * Первый экран нарисован — оболочка убирает сплэш (docs/notes/flutter-bridge.md ›
 * Сплэш). Без этого вызова она ждёт 10 секунд. Повторный вызов безопасен; вне
 * оболочки функции нет, и вызов ничего не делает.
 */
export function notifyAppReady(): void {
  try {
    window.notifyAppReady?.()
  } catch (error) {
    console.warn('[native] notifyAppReady failed', error)
  }
}

export interface NativeDateOptions {
  initialDate?: string
  minDate?: string
  maxDate?: string
}

interface DatePickerAnswer {
  ok?: unknown
  date?: unknown
  cancelled?: unknown
  error?: unknown
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/

/** Оболочка уже ответила не по контракту выбора даты — это старая сборка. */
let datePickerMissing = false

/** Дату выбирают в окне оболочки: она есть и не оказалась старой. */
export const hasNativeDatePicker = (): boolean => currentBridge() !== null && !datePickerMissing

/**
 * Окно выбора даты оболочки (`datePicker.open`). Дата — строка `YYYY-MM-DD` без
 * часового пояса, как в поле и в API. null — покупатель закрыл окно или оно уже
 * открыто (`BUSY`). undefined — оболочки нет или она ответила не тем (старая
 * сборка без обработчика): поле переходит на выбор браузера, и до перезапуска
 * окно оболочки больше не просим (`hasNativeDatePicker`).
 */
export async function pickNativeDate(
  options: NativeDateOptions,
): Promise<string | null | undefined> {
  if (!hasNativeDatePicker()) return undefined
  const args = Object.fromEntries(Object.entries(options).filter(([, value]) => Boolean(value)))
  const answer = (await callNative('datePicker.open', args)) as DatePickerAnswer | null | undefined
  if (answer?.ok === true && typeof answer.date === 'string' && DATE_ONLY.test(answer.date)) {
    return answer.date
  }
  if (answer?.ok === false && (answer.cancelled === true || answer.error === 'BUSY')) return null
  datePickerMissing = true
  return undefined
}

/** Для тестов: забыть ожидание готовности и ответ про окно даты. */
export function resetNativeBridge(): void {
  readyPromise = null
  datePickerMissing = false
}
